// Mock Data Service for Production Fallback
// This service provides mock data when backend services are unavailable

import { AISignal } from './ai-signals-api';

export interface MockSignalData {
  id: string;
  symbol: string;
  signal_type: 'BUY' | 'SELL' | 'HOLD';
  confidence_score: number;
  confidence_level: 'HIGH' | 'MEDIUM' | 'LOW';
  price_target: number;
  current_price: number;
  entry_price?: number;
  stop_loss?: number;
  take_profit?: number;
  risk_score: number;
  time_horizon: 'IMMEDIATE' | 'SHORT_TERM' | 'MEDIUM_TERM' | 'LONG_TERM';
  model_name: string;
  model_version: string;
  reasoning: string;
  technical_indicators: {
    rsi: number;
    macd: number;
    bollinger_position: string;
    volume_analysis: string;
    support_resistance: {
      support: number;
      resistance: number;
    };
  };
  market_sentiment: {
    overall: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
    fear_greed_index: number;
    social_sentiment: number;
  };
  portfolio_impact: {
    recommended_allocation: number;
    risk_adjustment: number;
    diversification_score: number;
  };
  status: 'ACTIVE' | 'EXECUTED' | 'EXPIRED' | 'CANCELLED';
  created_at: string;
  updated_at: string;
  expires_at?: string;
  executed_at?: string;
  performance_metrics: {
    profit_loss?: number;
    accuracy?: number;
    max_drawdown?: number;
    sharpe_ratio?: number;
  };
}

// Mock data generator
class MockDataService {
  private readonly mockSignals: MockSignalData[] = [
    {
      id: 'mock-signal-1',
      symbol: 'BTC',
      signal_type: 'BUY',
      confidence_score: 0.85,
      confidence_level: 'HIGH',
      price_target: 45000,
      current_price: 42000,
      entry_price: 41800,
      stop_loss: 40000,
      take_profit: 48000,
      risk_score: 25,
      time_horizon: 'SHORT_TERM',
      model_name: 'LSTM-Pro',
      model_version: 'v2.1',
      reasoning: 'Technical indicators show strong bullish momentum with RSI oversold recovery and MACD crossover. Volume analysis indicates institutional accumulation.',
      technical_indicators: {
        rsi: 68.5,
        macd: 245.3,
        bollinger_position: 'upper',
        volume_analysis: 'increasing',
        support_resistance: {
          support: 40000,
          resistance: 45000
        }
      },
      market_sentiment: {
        overall: 'BULLISH',
        fear_greed_index: 72,
        social_sentiment: 0.68
      },
      portfolio_impact: {
        recommended_allocation: 0.15,
        risk_adjustment: 0.8,
        diversification_score: 0.75
      },
      status: 'ACTIVE',
      created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
      updated_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 1000 * 60 * 90).toISOString(), // 1.5 hours from now
      performance_metrics: {
        accuracy: 0.78,
        max_drawdown: 0.05,
        sharpe_ratio: 1.2
      }
    },
    {
      id: 'mock-signal-2',
      symbol: 'SOL',
      signal_type: 'SELL',
      confidence_score: 0.72,
      confidence_level: 'MEDIUM',
      price_target: 85,
      current_price: 95,
      entry_price: 98,
      stop_loss: 105,
      take_profit: 80,
      risk_score: 35,
      time_horizon: 'IMMEDIATE',
      model_name: 'XGBoost-Ensemble',
      model_version: 'v1.8',
      reasoning: 'Price action shows exhaustion at resistance level. RSI overbought conditions and declining volume suggest potential reversal.',
      technical_indicators: {
        rsi: 78.2,
        macd: -12.5,
        bollinger_position: 'upper',
        volume_analysis: 'decreasing',
        support_resistance: {
          support: 85,
          resistance: 105
        }
      },
      market_sentiment: {
        overall: 'BEARISH',
        fear_greed_index: 45,
        social_sentiment: 0.32
      },
      portfolio_impact: {
        recommended_allocation: -0.08,
        risk_adjustment: 1.2,
        diversification_score: 0.65
      },
      status: 'ACTIVE',
      created_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(), // 15 minutes ago
      updated_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 1000 * 60 * 45).toISOString(), // 45 minutes from now
      performance_metrics: {
        accuracy: 0.69,
        max_drawdown: 0.08,
        sharpe_ratio: 0.9
      }
    },
    {
      id: 'mock-signal-3',
      symbol: 'ETH',
      signal_type: 'HOLD',
      confidence_score: 0.58,
      confidence_level: 'MEDIUM',
      price_target: 2800,
      current_price: 2750,
      risk_score: 45,
      time_horizon: 'MEDIUM_TERM',
      model_name: 'LSTM-Pro',
      model_version: 'v2.1',
      reasoning: 'Mixed signals across timeframes. Price consolidating near support level. Waiting for clearer directional bias before taking position.',
      technical_indicators: {
        rsi: 52.1,
        macd: 15.7,
        bollinger_position: 'middle',
        volume_analysis: 'stable',
        support_resistance: {
          support: 2700,
          resistance: 2850
        }
      },
      market_sentiment: {
        overall: 'NEUTRAL',
        fear_greed_index: 55,
        social_sentiment: 0.5
      },
      portfolio_impact: {
        recommended_allocation: 0,
        risk_adjustment: 1.0,
        diversification_score: 0.7
      },
      status: 'ACTIVE',
      created_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(), // 1 hour ago
      updated_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 1000 * 60 * 120).toISOString(), // 2 hours from now
      performance_metrics: {
        accuracy: 0.65,
        max_drawdown: 0.03,
        sharpe_ratio: 0.8
      }
    }
  ];

  // Get mock signals
  getMockSignals(limit = 10): AISignal[] {
    return this.mockSignals.slice(0, limit).map(signal => ({
      ...signal,
      // Remove mock-specific fields to match the AISignal interface
    })) as AISignal[];
  }

  // Get mock performance metrics
  getMockPerformanceMetrics() {
    return {
      total_signals: 156,
      success_rate: 0.73,
      avg_confidence: 0.68,
      total_return: 0.23,
      max_drawdown: 0.08,
      sharpe_ratio: 1.15,
      win_rate: 0.68,
      avg_holding_period: 2.5,
      last_updated: new Date().toISOString()
    };
  }

  // Get mock signal stats
  getMockSignalStats() {
    return {
      total_signals: 156,
      active_signals: 12,
      executed_today: 8,
      success_rate: 73.2,
      avg_confidence: 68.5,
      top_performing_model: 'LSTM-Pro v2.1'
    };
  }

  // Get mock risk metrics
  getMockRiskMetrics() {
    return {
      overall_portfolio_risk: 32,
      signal_concentration_risk: 28,
      market_correlation_risk: 45,
      volatility_risk: 38,
      liquidity_risk: 15,
      risk_score_breakdown: [
        { factor: 'Market Volatility', score: 35, impact: 'MEDIUM' },
        { factor: 'Position Concentration', score: 28, impact: 'LOW' },
        { factor: 'Correlation Risk', score: 45, impact: 'HIGH' },
        { factor: 'Liquidity Risk', score: 15, impact: 'LOW' }
      ],
      recommended_adjustments: [
        {
          type: 'REDUCE',
          asset: 'BTC',
          current_allocation: 0.35,
          recommended_allocation: 0.28,
          reason: 'High concentration risk'
        },
        {
          type: 'INCREASE',
          asset: 'ETH',
          current_allocation: 0.15,
          recommended_allocation: 0.22,
          reason: 'Diversification improvement'
        }
      ]
    };
  }

  // Simulate network delay for realistic experience
  async simulateDelay(ms = 500): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Check if mock data should be used
  shouldUseMockData(): boolean {
    return import.meta.env.VITE_USE_MOCK_DATA === 'true' || 
           import.meta.env.PROD === true;
  }
}

// Export singleton instance
export const mockDataService = new MockDataService();